import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import {
  type DatabaseField,
  type DatabaseValue,
  type RawDatabaseValue,
} from '@databases/types/database';
import {
  type DatabaseChange,
  type ValueChange,
} from '@databases/utils/database/changesets';
import { convertType } from '@databases/utils/database/field-types';
import composeRefs from '@seznam/compose-react-refs';
import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { getFieldType } from '../fields';
import { useLoadDependencies } from '../utils';

import { BulkEditProvider } from './bulk-edit-context';

export interface FieldValueEditProps {
  contentId: string;
  entryId?: string;
  field: DatabaseField;
  value: DatabaseValue;
  onChange: (value: DatabaseValue, change: ValueChange | null) => void;
  onFocus?: (e?: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e?: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isSeamless?: boolean;
  addChangeToDraft?: (change: DatabaseChange) => void;
  noValidation?: boolean;
  isDefaultValue?: boolean;
  placeholder?: string;
  isBulkEdit?: boolean;
  appearance?: 'default' | 'none' | 'subtle' | undefined;
  isFilterValueEdit?: boolean;
  isRenderedInSidePanel?: boolean;
  ariaLabel?: string;
  initialKey?: string;
}

/**
 * Renders the edit form for a field as a database value based on the field type.
 */
export const FieldValueEdit = forwardRef<HTMLInputElement, FieldValueEditProps>(
  function FieldValueEdit(
    { contentId, entryId, field, value, onChange, onBlur, onFocus, ariaLabel, ...props },
    inputRef
  ) {
    const fieldType = getFieldType(field.type);
    const Component = (fieldType.popoverEdit ?? fieldType.edit) as any;

    const { formatMessage } = useIntl();
    if (!Component) {
      throw new Error(
        formatMessage(messages['error-message.unknown-field-type'], {
          fieldType: field.type,
        })
      );
    }

    const handleChange = useCallback(
      (val: RawDatabaseValue, change: ValueChange | null) => {
        onChange({ type: field.type, value: val } as DatabaseValue, change);
      },
      [field.type, onChange]
    );

    const store = defaultStoreUtils.useStore();

    // Not using useSelector() here, as some conversions generate random IDs, meaning that the converted value changes each time.
    // This way we only run the conversion when the original value changes.
    const convertedValue = useMemo(() => {
      return convertType({ field, value }, field.type, store.getState());
    }, [field, store, value]);

    useLoadDependencies({ contentId, entryId, field, convertedValue }, store.getState());

    const ref = useRef();

    const renderComponent = useCallback(
      () => (
        <Component
          field={field}
          value={convertedValue.value}
          onChange={handleChange}
          contentId={contentId}
          entryId={entryId}
          ref={composeRefs(ref, inputRef)}
          onFocus={onFocus}
          onBlur={onBlur}
          ariaLabel={ariaLabel}
          {...props}
        />
      ),
      [
        Component,
        field,
        convertedValue.value,
        handleChange,
        contentId,
        entryId,
        inputRef,
        onFocus,
        onBlur,
        ariaLabel,
        props,
      ]
    );

    return (
      <BulkEditProvider value={props.isBulkEdit ?? false}>
        <ValidationStatus error={undefined}>{renderComponent}</ValidationStatus>
      </BulkEditProvider>
    );
  }
);
